/** @jsxImportSource @emotion/react */
import {
  ColumnFiltersState,
  FilterFn,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { FilterBar } from "components/FilterBar/FilterBar";
import { ActivityTable } from "components/TableContainer/ActivityTable";
import { useActivityColumnDefinitions } from "hooks/useActivityColumnDefinitions";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { VariableSizeList as List } from "react-window";
import { TransactionDetail } from "types/history";
import { mainnet } from "viem/chains";
import { ActivityHeader } from "../../components/ActivityLayout/ActivityHeader";
import DownloadCSVButton from "../../components/DownloadCSVButton/DownloadCSVButton";
import { Spacer } from "../../components/general/Spacer/Spacer";
import { Filter, FiltersInterface, TransactionType } from "../../config/consts";
import { FlexBetweenContainer } from "../../containers/FlexBetweenContainer";
import { useIsTradeHistoryReturned } from "../../hooks/useIsTradeHistoryReturned";
import { useTradeHistoryGroupedData } from "../../hooks/useTradeHistoryGroupedData";
import { useUpdateAddressWithENS } from "../../hooks/useUpdateAddressWithENS";
import { ActivityStore } from "../../store/activity/ActivityStore";
import { getAllTokenListsInclInactiveTokens } from "../../store/activity/getAllTokensInclInactiveTokens";
import { GlobalStore } from "../../store/global/GlobalStore";
import { getOrderTypeName } from "../../utils/orderTypeUtils";
import { ActivityContainer } from "./elements/ActivityContainer";
import { ConnectWalletCTA } from "./elements/ConnectWalletCTA";
import { ErrorDisplay } from "./elements/ErrorDisplay";
import { NoActivityYetDisplay } from "./elements/NoActivityYetDisplay";
import { Helmet } from "react-helmet";
import { Hash } from "viem";
import { getAllTokenLists } from "../../store/global/getAllTokenLists";
import { css } from "@emotion/react";
import { CHAINS } from "config/chains";
import { getHistory } from "../../store/activity/getHistory";

export const Activity: React.FC = () => {
  const theme = GlobalStore.useState(s => s.theme);
  const userAddress = GlobalStore.useState(s => s.address);
  const history = ActivityStore.useState(s => s.history);
  const [confirmedFilters, setConfirmedFilters] = useState<FiltersInterface>(FILTERS_INITIAL_STATE);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const isTradeHistoryReturned = useIsTradeHistoryReturned();
  const colDefs = useActivityColumnDefinitions();
  const virtualizedListRef = useRef<List>(null);

  const networkName = GlobalStore.useState(s => s.network.name);
  useUpdateAddressWithENS();
  /* Calls all 3 tokens endpoints eth, poly, arb for token decimals this is for /activity */
  getAllTokenListsInclInactiveTokens.useBeckon(networkName);
  getAllTokenLists.useBeckon();

  const groupedData = useTradeHistoryGroupedData();
  const tableData = React.useMemo(
    () => (groupedData ? Object.values(groupedData).flat() : []),
    [groupedData]
  );

  useEffect(() => {
    if (userAddress) {
      const TODAY_TIMESTAMP = new Date().getTime() * 1e6;
      void getHistory.run({
        address: userAddress as `0x${string}`,
        timestamp: TODAY_TIMESTAMP, // Timestamp needs to be provided in nanoseconds due to DB getTime returns milli then we convert to nano by multiplying
        previousResult: undefined,
      });
    }
  }, [userAddress]);

  const table = useReactTable<TransactionDetail>({
    data: tableData,
    columns: colDefs,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    filterFns: {
      network: networkFilter,
      transaction: transactionFilter,
      token: tokenFilter,
      amount: amountFilter,
    },
  });
  const csvData = useMemo(() => {
    return tableData.map(item => {
      // We remove chain_id and status from the CSV
      const chainName = CHAINS[item.chain_id]?.displayName;
      return {
        "Time and Date": item.timestamp,
        "Tokens Sold": Object.keys(item.sellTokens).toString(),
        "Tokens Bought": Object.keys(item.buyTokens).toString(),
        "Volume (USD)": item.volumeUsd,
        "Network Name": chainName,
        "Transaction Hash": item.txHash,
        Trader: item.sender,
        Receiver: item.receiver,
        "Trade Type": getOrderTypeName(item.type),
        "Included Network Cost (USD)": item.gasFeeUsd,
      };
    });
  }, [tableData]);

  const content = useMemo(() => {
    if (!userAddress) {
      return <ConnectWalletCTA />;
    }

    if (history.error != null) {
      return (
        <ActivityContainer theme={theme}>
          <ActivityHeader />
          <ErrorDisplay />
        </ActivityContainer>
      );
    }

    if (history.data != null && Object.keys(history.data.results).length === 0) {
      return <NoActivityYetDisplay />;
    }

    return (
      <ActivityContainer theme={theme}>
        <FlexBetweenContainer
          css={css`
            min-height: 38px;
          `}
        >
          <ActivityHeader />
          <DownloadCSVButton
            data={csvData}
            filename={`bebop_activity_${downloadCsvFilename()}`}
            isTradeHistoryReturned={isTradeHistoryReturned}
          />
        </FlexBetweenContainer>
        <Spacer height={12} />
        <FilterBar
          table={table}
          confirmedFilters={confirmedFilters}
          setConfirmedFilters={setConfirmedFilters}
          scrollToListTop={() => {
            virtualizedListRef.current?.scrollToItem(0);
          }}
        />
        <ActivityTable
          table={table}
          confirmedFilters={confirmedFilters}
          clearAllFilters={() => {
            table.resetColumnFilters();
            setConfirmedFilters(FILTERS_INITIAL_STATE);
          }}
          virtualizedListRef={virtualizedListRef}
        />
      </ActivityContainer>
    );
  }, [
    confirmedFilters,
    csvData,
    history.data,
    history.error,
    isTradeHistoryReturned,
    table,
    theme,
    userAddress,
  ]);

  return (
    <>
      <Helmet>
        <title>Bebop - Activity</title>
        <meta
          name="description"
          content={"Trade all of your favorite token in one go, at best prices."}
        />
        <link rel="canonical" href="https://bebop.xyz/activity" />
      </Helmet>
      {content}
    </>
  );
};

// Helpers

const downloadCsvFilename = () => {
  const date = new Date();
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based, so we add 1
  const year = date.getFullYear();
  return `${day}${month}${year}`;
};

const networkFilter: FilterFn<TransactionDetail> = (
  row,
  columnId,
  value: FiltersInterface["network"]
) => {
  const networkId: number = row.getValue(columnId);
  const networkName = CHAINS[networkId].displayName ?? mainnet.name;
  // value = ["Arbitrum", "Polygon", "Ethereum"]
  return value?.includes(networkName) ?? false;
};

const transactionFilter: FilterFn<TransactionDetail> = (
  row,
  columnId,
  value: FiltersInterface["transaction"]
) => {
  const transaction: {
    sender: TransactionDetail["sender"];
    receiver: TransactionDetail["receiver"];
  } = row.getValue(columnId);
  const transactionType = getTransactionType(transaction.sender, transaction.receiver);
  return value?.includes(transactionType) ?? false;
};

const tokenFilter: FilterFn<TransactionDetail> = (
  row,
  columnId,
  value: FiltersInterface["token"]
) => {
  const tokens: Array<Hash> = row.getValue(columnId);
  return value?.some(token => tokens.includes(token)) ?? false;
};

const amountFilter: FilterFn<TransactionDetail> = (
  row,
  columnId,
  value: FiltersInterface["amount"]
) => {
  const amount: TransactionDetail["volumeUsd"] = row.getValue(columnId);
  if (value == null) return true;
  if (value.min != null && amount.value.lessThan(value.min.value)) return false;
  if (value.max != null && amount.value.greaterThan(value.max.value)) return false;
  return true;
};

function getTransactionType(
  sender: TransactionDetail["sender"],
  receiver: TransactionDetail["receiver"]
) {
  if (sender !== receiver) return TransactionType.TradeAndSend;
  return TransactionType.Trade;
}

export const FILTERS_INITIAL_STATE = {
  [Filter.Network]: undefined,
  [Filter.Transaction]: undefined,
  [Filter.Token]: undefined,
  [Filter.Amount]: undefined,
  [Filter.Date]: undefined,
};
Activity.whyDidYouRender = true;
